import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../../utils/formatters/date";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Snackbar,
  Checkbox,
  FormHelperText,
  Divider,
} from "@mui/material";

// MUI v7 Grid (v2)
import Grid from "@mui/material/Grid";

import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as MedicalIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,
  RateReview as ReviewIcon,
} from "@mui/icons-material";

import { PatientSearch } from "../../shared";
import { AuthContext } from "../../../context/AuthContext/AuthContext";

import {
  createReferral as createReferralApi,
  updateReferral as updateReferralApi,
  getReferralById as getReferralByIdApi,
  getSpecialistTypes,
  getCareManagers,
  searchSpecialistsDirectory,
  searchDiagnoses,
  searchHedisCodes,
  searchReferralReasons,
  getPatientDemographics,
  getPatientMemberships,
  getCommentsByReferral,
  createComment as createCommentApi,
  deleteComment as deleteCommentApi,
} from "../services/specialistReferralsService";
import {
  getStatuses,
  getLocations,
  getPcps,
  getMedicalDirectors,
} from "../../../utils/services/control/masterDataService";

import { formatPhoneNumber } from "../constants/referralOptions";

import { debounce } from "../utils/debounce";

// Initial form state — ID-based for API fields
const getInitialFormState = () => ({
  // Patient Information
  cipId: "",
  memberId: "",
  memberFullName: "",
  dob: "",
  memberCondition: "ACTV",
  language: "",
  memberEmail: "",
  memberPhone: "",
  memberMobile: "",
  addressLine1: "",

  // Clinical Information
  urgencyLevelId: "",
  diagnoses: [], // Cambio 1 — [{ codeDiagnosis, description, isPrimary }]
  reasonForReferral: "",
  hedisCodes: "",
  hasRubiconBeenUsed: false,
  lab: false,
  imaging: false,
  patientCodeStatusId: "", // Cambio 7 — código CPT del paciente (opcional)

  // Specialist Information
  specialistTypeId: "",
  specialistId: null,
  specialistName: "",
  specialistNpi: "",
  specialistPhone: "",
  specialistFax: "",
  specialistAddress: "",

  // Administrative Information
  needsCareManager: false, // Cambio 3
  careManagerId: "",
  referralRequestId: "", // Cambio 4
  pcpId: "",
  pcpName: "",
  locationId: "",
  medicalCenter: "",
  employeeStaffId: null,

  // Status and Follow Up
  referralStatusId: "",
  followupStatusId: "",
  followupNotes: "",
  availityReferral: "",

  // Medical Director Review (Cambio 5)
  medicalDirectorStatusId: "",
  medicalDirectorFollowupId: "",
  medicalDirectorId: "",
  directorReviewed: "",
});

export default function ReferralCreatePage() {
  const navigate = useNavigate();
  const { referralId } = useParams();
  const isEditing = Boolean(referralId);

  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(getInitialFormState());
  // Paciente seleccionado para alimentar el componente transversal PatientSearch
  // (ficha + tarjeta de tabs). Solo el cipId se usa al guardar.
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Catalog states (loaded from API)
  const [urgencyLevels, setUrgencyLevels] = useState([]);
  const [referralStatuses, setReferralStatuses] = useState([]);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [specialistTypeOptions, setSpecialistTypeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [careManagerOptions, setCareManagerOptions] = useState([]);
  const [pcpOptions, setPcpOptions] = useState([]);
  // Cambios 4 y 5 — catálogos nuevos
  const [referralRequestOptions, setReferralRequestOptions] = useState([]);
  const [mdStatusOptions, setMdStatusOptions] = useState([]);
  const [mdFollowupOptions, setMdFollowupOptions] = useState([]);
  const [medicalDirectorOptions, setMedicalDirectorOptions] = useState([]);
  // Cambio 7 — catálogo Patient Code Status (CPT), grupo `patient_code_status`
  const [patientCodeStatusOptions, setPatientCodeStatusOptions] = useState([]);

  // Autocomplete search states
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagnosisInput, setDiagnosisInput] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [hedisOptions, setHedisOptions] = useState([]);
  const [hedisLoading, setHedisLoading] = useState(false);
  const [specialistDirOptions, setSpecialistDirOptions] = useState([]);
  const [specialistDirLoading, setSpecialistDirLoading] = useState(false);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);

  // Accordion expanded states
  const [expanded, setExpanded] = useState({
    patient: true,
    clinical: true,
    specialist: true,
    admin: true,
    status: true,
    medicalDirector: false,
    comments: false,
  });

  // Debounced search refs
  const diagnosisSearchRef = useRef(null);
  const reasonSearchRef = useRef(null);
  const hedisSearchRef = useRef(null);
  const specialistDirSearchRef = useRef(null);
  // Secuencia para descartar respuestas viejas de la búsqueda de diagnósticos
  // (el SP_SEARCH_DIAGNOSES del backend es lento; sin esto el spinner se queda
  // atascado mostrando resultados de una búsqueda anterior).
  const diagnosisReqRef = useRef(0);

  // Initialize debounced search functions
  useEffect(() => {
    diagnosisSearchRef.current = debounce(async (term) => {
      // Mínimo 3 caracteres: con 2 el LIKE '%xx%' del backend devuelve
      // demasiadas filas y la consulta es aún más lenta.
      if (!term || term.trim().length < 3) { setDiagnosisOptions([]); return; }
      const reqId = ++diagnosisReqRef.current;
      setDiagnosisLoading(true);
      // Limpia resultados viejos de inmediato — si no, mientras el backend
      // responde (lento) el dropdown sigue mostrando la búsqueda anterior.
      setDiagnosisOptions([]);
      try {
        const results = await searchDiagnoses(term.trim());
        // Solo la última búsqueda dispara actualización de estado.
        if (reqId === diagnosisReqRef.current) setDiagnosisOptions(results);
      } catch (e) {
        console.error("Search failed:", e?.message);
      } finally {
        if (reqId === diagnosisReqRef.current) setDiagnosisLoading(false);
      }
    }, 450);

    reasonSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setReasonOptions([]); return; }
      setReasonLoading(true);
      try {
        const results = await searchReferralReasons(term);
        setReasonOptions(results);
      } catch (e) { console.error("Search failed:", e?.message); }
      finally { setReasonLoading(false); }
    }, 300);

    hedisSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setHedisOptions([]); return; }
      setHedisLoading(true);
      try {
        const results = await searchHedisCodes(term);
        setHedisOptions(results);
      } catch (e) { console.error("Search failed:", e?.message); }
      finally { setHedisLoading(false); }
    }, 300);

    specialistDirSearchRef.current = debounce(async (term, specTypeId) => {
      if (!term || term.length < 2) { setSpecialistDirOptions([]); return; }
      setSpecialistDirLoading(true);
      try {
        const results = await searchSpecialistsDirectory(term, specTypeId || undefined);
        setSpecialistDirOptions(results);
      } catch (e) { console.error("Search failed:", e?.message); }
      finally { setSpecialistDirLoading(false); }
    }, 300);
  }, []);

  // Load catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [
          urgency, status, followup, specTypes, locs, cms, pcps,
          referralRequests, mdStatus, mdFollowup, directors, patientCodeStatuses,
        ] = await Promise.all([
          getStatuses("specialist_referral_urgency"),
          getStatuses("specialist_referral_status"),
          getStatuses("specialist_referral_followup"),
          getSpecialistTypes(),
          getLocations({ clinical: true, status: 1 }),
          getCareManagers(),
          getPcps({ status: 1 }),
          getStatuses("specialist_referral_request", false),
          getStatuses("medical_director_status", false),
          getStatuses("medical_director_followup", false),
          getMedicalDirectors(),
          getStatuses("patient_code_status", false), // Cambio 7
        ]);
        setUrgencyLevels(urgency || []);
        setReferralStatuses(status || []);
        setFollowupStatuses(followup || []);
        setSpecialistTypeOptions(specTypes || []);
        setLocationOptions(locs || []);
        setCareManagerOptions(cms || []);
        setPcpOptions(pcps || []);
        setReferralRequestOptions(referralRequests || []);
        setMdStatusOptions(mdStatus || []);
        setMdFollowupOptions(mdFollowup || []);
        setMedicalDirectorOptions(directors || []);
        setPatientCodeStatusOptions(patientCodeStatuses || []); // Cambio 7

        // Set defaults for new referral
        if (!isEditing) {
          // Default urgency to first option
          if (urgency?.length > 0) {
            setForm((prev) => ({ ...prev, urgencyLevelId: prev.urgencyLevelId || urgency[0].statusId }));
          }
          // Default status to first option
          if (status?.length > 0) {
            setForm((prev) => ({ ...prev, referralStatusId: prev.referralStatusId || status[0].statusId }));
          }
          // Default followup to first option
          if (followup?.length > 0) {
            setForm((prev) => ({ ...prev, followupStatusId: prev.followupStatusId || followup[0].statusId }));
          }
          // Set employeeStaffId from logged-in user
          const staffId = user?.employeeId || user?.employee;
          if (staffId) {
            setForm((prev) => ({ ...prev, employeeStaffId: Number(staffId) }));
          }
        }
      } catch (err) {
        console.error("Error loading catalogs:", err?.message);
      }
    };
    loadCatalogs();
  }, [isEditing, user]);

  // Hydrates patient-personal fields from the demographics + memberships endpoints.
  // keepReferralAssignment: when true (edit mode), preserves PCP/location/medicalCenter
  // already loaded from the saved referral instead of overwriting with current demographics.
  const fetchAndFillPatientData = useCallback(
    async (cipId, { keepReferralAssignment = false } = {}) => {
      if (!cipId) return;
      setLoadingPatient(true);
      try {
        const [demographics, memberships] = await Promise.all([
          getPatientDemographics(cipId),
          getPatientMemberships(cipId),
        ]);

        setForm((prev) => {
          const next = {
            ...prev,
            cipId: demographics.cipId || cipId,
            memberFullName: demographics.memberFullName || prev.memberFullName,
            dob: demographics.dob ? demographics.dob.split("T")[0] : prev.dob,
            memberEmail: demographics.memberEmail || "",
            memberPhone: demographics.memberPhoneNumber || prev.memberPhone || "",
            memberMobile: demographics.memberMobileNumber || "",
            addressLine1: demographics.addressLine1 || "",
            memberId: memberships?.[0]?.memberId || prev.memberId || "",
          };
          if (!keepReferralAssignment) {
            next.pcpId = demographics.pcpEmployeeId || "";
            next.pcpName = demographics.pcpFullName || "";
            next.locationId = demographics.locationId || "";
            next.medicalCenter = demographics.medicalCenter || "";
          }
          return next;
        });

        // Alimenta la tarjeta de detalle (PatientDetailCard) con el shape que
        // espera: mismos nombres de campo que el Patient Map V2. En edit mode
        // este es el único origen de la ficha (no hubo selección en buscador).
        setSelectedPatient((prev) => ({
          ...(prev || {}),
          cipId: demographics.cipId || cipId,
          memberId: memberships?.[0]?.memberId || prev?.memberId || "",
          memberType: memberships?.[0]?.memberType || prev?.memberType || null,
          memberFullName: demographics.memberFullName || prev?.memberFullName || "",
          dateOfBirth: demographics.dob || prev?.dateOfBirth || null,
          age: demographics.age ?? prev?.age ?? null,
          gender: demographics.gender || prev?.gender || null,
          email: demographics.memberEmail || prev?.email || "",
          phoneNumber: demographics.memberPhoneNumber || prev?.phoneNumber || "",
          mobileNumber: demographics.memberMobileNumber || prev?.mobileNumber || "",
          status: demographics.status ?? prev?.status ?? null,
          pcpName: demographics.pcpFullName || prev?.pcpName || "",
          pcpTitle: demographics.pcpTitle || prev?.pcpTitle || "",
          locationId: demographics.locationId ?? prev?.locationId ?? null,
          medicalCenter: demographics.medicalCenter || prev?.medicalCenter || "",
          locationAddress:
            [demographics.addressLine1, demographics.cityName, demographics.state, demographics.zipCode]
              .filter(Boolean)
              .join(", ") || prev?.locationAddress || "",
        }));
      } catch (err) {
        console.error("Error fetching patient details:", err?.message);
      } finally {
        setLoadingPatient(false);
      }
    },
    []
  );

  // Load existing referral for edit mode
  useEffect(() => {
    if (!isEditing || !referralId) return;
    const loadReferral = async () => {
      setLoading(true);
      try {
        const [referral, commentsData] = await Promise.all([
          getReferralByIdApi(Number(referralId)),
          getCommentsByReferral(Number(referralId)),
        ]);

        // Seed form with the referral's saved fields. Patient-personal fields not stored on
        // the referral (email, mobile, address, language, condition) are filled below from
        // current patient demographics.
        setForm({
          cipId: referral.cipId || "",
          memberId: referral.memberId || "",
          memberFullName: referral.patientName || "",
          dob: referral.patientDob ? referral.patientDob.split("T")[0] : "",
          memberCondition: "ACTV",
          language: "",
          memberEmail: "",
          memberPhone: referral.patientPhone || "",
          memberMobile: "",
          addressLine1: "",
          urgencyLevelId: referral.urgencyLevelId || "",
          // Cambio 1 — diagnósticos vienen poblados desde el backend
          diagnoses: Array.isArray(referral.diagnoses)
            ? referral.diagnoses.map((d) => ({
                codeDiagnosis: d.codeDiagnosis,
                description: d.description || "",
                isPrimary: !!d.isPrimary,
              }))
            : [],
          reasonForReferral: referral.reasonForReferral || "",
          hedisCodes: referral.hedisCodes || "",
          hasRubiconBeenUsed: referral.hasRubiconBeenUsed ?? false,
          lab: referral.lab ?? false,
          imaging: referral.imaging ?? false,
          patientCodeStatusId: referral.patientCodeStatusId || "", // Cambio 7
          specialistTypeId: referral.specialistTypeId || "",
          specialistId: referral.specialistId || null,
          specialistName: referral.specialistName || "",
          specialistNpi: referral.specialistNpi || "",
          specialistPhone: referral.specialistPhone || "",
          specialistFax: referral.specialistFax || "",
          specialistAddress: referral.specialistAddress || "",
          needsCareManager: referral.needsCareManager ?? false,
          careManagerId: referral.careManagerId || "",
          referralRequestId: referral.referralRequestId || "",
          pcpId: referral.pcpId || "",
          pcpName: referral.pcpName || "",
          locationId: referral.locationId || "",
          medicalCenter: referral.medicalCenter || "",
          employeeStaffId: referral.employeeStaffId || null,
          referralStatusId: referral.referralStatusId || "",
          followupStatusId: referral.followupStatusId || "",
          followupNotes: referral.followupNotes || "",
          availityReferral: referral.availityReferral ? referral.availityReferral.split("T")[0] : "",
          medicalDirectorStatusId: referral.medicalDirectorStatusId || "",
          medicalDirectorFollowupId: referral.medicalDirectorFollowupId || "",
          medicalDirectorId: referral.medicalDirectorId || "",
          directorReviewed: referral.directorReviewed ? referral.directorReviewed.split("T")[0] : "",
        });
        setComments(commentsData || []);

        // Expand Medical Director section if it already has data
        if (
          referral.medicalDirectorStatusId ||
          referral.medicalDirectorFollowupId ||
          referral.medicalDirectorId ||
          referral.directorReviewed
        ) {
          setExpanded((prev) => ({ ...prev, medicalDirector: true }));
        }

        // Hydrate patient-personal fields from current demographics. Keep PCP/location
        // as saved in the referral (they reflect the assignment when it was created).
        if (referral.cipId) {
          await fetchAndFillPatientData(referral.cipId, { keepReferralAssignment: true });
        }
      } catch (err) {
        console.error("Error loading referral:", err?.message);
        navigate("/specialist-referrals", {
          state: { refresh: true, message: "Error loading referral" },
        });
      } finally {
        setLoading(false);
      }
    };
    loadReferral();
  }, [isEditing, referralId, navigate, fetchAndFillPatientData]);

  const handleChange = useCallback(
    (field, value) => {
      setForm((prev) => {
        const newForm = { ...prev, [field]: value };

        // When location changes, refresh the medical center label
        if (field === "locationId") {
          const loc = locationOptions?.find((l) => l.locationId === value);
          newForm.medicalCenter = loc?.medicalCenter || "";
        }

        // Cambio 3 — when "Needs Care Manager" is turned off, clear the Care Manager
        if (field === "needsCareManager" && value === false) {
          newForm.careManagerId = "";
        }

        return newForm;
      });

      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    },
    [errors, locationOptions]
  );

  const handleToggle = (field, value) => {
    if (value !== null) handleChange(field, value);
  };

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // ── Cambio 1 — Diagnoses handlers ────────────────────────────
  const handleAddDiagnosis = (option) => {
    if (!option || typeof option === "string" || !option.code) return;
    // El catálogo del backend devuelve el código como CHAR con espacios al
    // final (ej. "A009    "); se recorta antes de guardarlo y enviarlo.
    const code = option.code.trim();
    if (!code) return;
    setForm((prev) => {
      if (prev.diagnoses.some((d) => d.codeDiagnosis === code)) {
        setSnackbar({
          open: true,
          message: `${code} is already in the list.`,
          severity: "warning",
        });
        return prev;
      }
      return {
        ...prev,
        diagnoses: [
          ...prev.diagnoses,
          { codeDiagnosis: code, description: (option.description || "").trim(), isPrimary: false },
        ],
      };
    });
    if (errors.diagnoses) setErrors((prev) => ({ ...prev, diagnoses: null }));
  };

  const handleToggleDiagnosisPrimary = (codeDiagnosis) => {
    setForm((prev) => ({
      ...prev,
      diagnoses: prev.diagnoses.map((d) =>
        d.codeDiagnosis === codeDiagnosis ? { ...d, isPrimary: !d.isPrimary } : d
      ),
    }));
  };

  const handleRemoveDiagnosis = (codeDiagnosis) => {
    setForm((prev) => ({
      ...prev,
      diagnoses: prev.diagnoses.filter((d) => d.codeDiagnosis !== codeDiagnosis),
    }));
  };

  // Selección del paciente desde el componente transversal PatientSearch.
  // La ficha que entrega (Patient Map V2) trae todo; aquí solo necesitamos el
  // cipId para guardar, pero también prellenamos los campos de display.
  const handlePatientSelect = async (patient) => {
    if (!patient) {
      setSelectedPatient(null);
      setForm((prev) => ({
        ...prev,
        cipId: "",
        memberId: "",
        memberFullName: "",
        dob: "",
        memberCondition: "ACTV",
        memberEmail: "",
        memberPhone: "",
        memberMobile: "",
        addressLine1: "",
        pcpId: "",
        pcpName: "",
        locationId: "",
        medicalCenter: "",
      }));
      return;
    }

    // Muestra la ficha/tarjeta de inmediato (la ficha ya trae status, etc.).
    setSelectedPatient(patient);

    const cipId = patient.cipId || patient.memberId || "";
    setForm((prev) => ({
      ...prev,
      cipId,
      memberFullName: patient.memberFullName || patient.fullName || "",
    }));

    await fetchAndFillPatientData(cipId);
  };

  // Specialist directory selection
  const handleSpecialistSelect = (_event, specialist) => {
    setSelectedSpecialist(specialist);
    if (specialist) {
      handleChange("specialistId", specialist.specialistId);
      handleChange("specialistName", specialist.specialistName || "");
      handleChange("specialistNpi", specialist.npi || "");
      handleChange("specialistPhone", specialist.phone || "");
      handleChange("specialistFax", specialist.fax || "");
      handleChange("specialistAddress",
        [specialist.address, specialist.city, specialist.state, specialist.zipCode]
          .filter(Boolean)
          .join(", ")
      );
      if (specialist.specialistTypeId) {
        handleChange("specialistTypeId", specialist.specialistTypeId);
      }
    }
  };

  // HEDIS multi-select
  const handleHedisChange = (_event, newValue) => {
    const codes = newValue
      .map((v) => (typeof v === "string" ? v : v.hedisCode))
      .filter(Boolean);
    handleChange("hedisCodes", codes.join(","));
  };

  // Comments — Add via API (only in edit mode)
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    if (isEditing && referralId) {
      try {
        await createCommentApi({
          specialistReferralId: Number(referralId),
          comment: newComment,
        });
        // Refresh comments
        const updated = await getCommentsByReferral(Number(referralId));
        setComments(updated || []);
      } catch (err) {
        console.error("Error adding comment:", err?.message);
        setSnackbar({ open: true, message: "Error adding comment", severity: "error" });
      }
    } else {
      // For new referral, store locally (will be lost if not saved)
      setComments((prev) => [
        ...prev,
        {
          commentId: `local-${Date.now()}`,
          comment: newComment,
          createdBy: user?.name || "Staff",
          createDate: new Date().toISOString(),
        },
      ]);
    }

    setNewComment("");
    setCommentDialogOpen(false);
  };

  // Delete comment via API
  const handleDeleteComment = async (commentId) => {
    if (typeof commentId === "string" && commentId.startsWith("local-")) {
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
      return;
    }
    try {
      await deleteCommentApi(commentId);
      setComments((prev) => prev.filter((c) => c.commentId !== commentId));
    } catch (err) {
      console.error("Error deleting comment:", err?.message);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!form.cipId?.trim()) newErrors.cipId = "Patient is required";
    if (!form.memberFullName?.trim()) newErrors.memberFullName = "Member name is required";
    if (!form.urgencyLevelId) newErrors.urgencyLevelId = "Urgency level is required";

    // Cambio 1 — al menos un diagnóstico, y al menos uno primario
    if (form.diagnoses.length === 0) {
      newErrors.diagnoses = "At least one diagnosis is required";
    } else if (!form.diagnoses.some((d) => d.isPrimary)) {
      newErrors.diagnoses = "At least one primary diagnosis is required";
    }

    if (!form.reasonForReferral?.trim()) newErrors.reasonForReferral = "Reason for referral is required";
    if (!form.specialistTypeId) newErrors.specialistTypeId = "Specialist type is required";
    if (!form.referralRequestId) newErrors.referralRequestId = "Referral request is required";
    if (!form.pcpId) newErrors.pcpId = "Provider name is required";
    if (!form.locationId) newErrors.locationId = "Center is required";
    if (!form.referralStatusId) newErrors.referralStatusId = "Status is required";

    // Cambio 3 — Care Manager obligatorio si el toggle está activo
    if (form.needsCareManager && !form.careManagerId) {
      newErrors.careManagerId = "Care Manager is required when 'Needs Care Manager' is enabled";
    }

    setErrors(newErrors);
    return { ok: Object.keys(newErrors).length === 0, newErrors };
  };

  // Save handler — API
  const handleSave = async () => {
    const { ok, newErrors } = validateForm();
    if (!ok) {
      // Expand sections with errors
      const sectionsWithErrors = {
        patient: ["cipId", "memberFullName", "memberEmail"].some((f) => !!newErrors[f]),
        clinical: ["urgencyLevelId", "diagnoses", "reasonForReferral"].some((f) => !!newErrors[f]),
        specialist: ["specialistTypeId"].some((f) => !!newErrors[f]),
        admin: ["pcpId", "locationId", "careManagerId", "referralRequestId"].some((f) => !!newErrors[f]),
        status: ["referralStatusId"].some((f) => !!newErrors[f]),
      };
      setExpanded((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(sectionsWithErrors)
            .filter(([, v]) => v)
            .map(([k]) => [k, true])
        ),
      }));
      setSnackbar({ open: true, message: "Please fix the required fields.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const toInt = (val) => (val ? Number(val) : null);

      const payload = {
        cipId: form.cipId,
        memberId: form.memberId || null,
        urgencyLevelId: toInt(form.urgencyLevelId),
        icd10Codes: null, // Cambio 1 — deprecado, siempre null
        reasonForReferral: form.reasonForReferral || null,
        hedisCodes: form.hedisCodes || null,
        hasRubiconBeenUsed: form.hasRubiconBeenUsed ?? false,
        lab: form.lab ?? false,
        imaging: form.imaging ?? false,
        patientCodeStatusId: toInt(form.patientCodeStatusId), // Cambio 7

        specialistTypeId: toInt(form.specialistTypeId),
        specialistId: toInt(form.specialistId),
        specialistName: form.specialistName || null,
        specialistNpi: form.specialistNpi || null,
        specialistPhone: form.specialistPhone || null,
        specialistFax: form.specialistFax || null,
        specialistAddress: form.specialistAddress || null,
        needsCareManager: form.needsCareManager ?? false, // Cambio 3
        careManagerId: form.needsCareManager ? toInt(form.careManagerId) : null,
        referralRequestId: toInt(form.referralRequestId), // Cambio 4
        pcpId: toInt(form.pcpId),
        locationId: toInt(form.locationId),
        employeeStaffId: toInt(form.employeeStaffId) || toInt(user?.employeeId || user?.employee),
        referralStatusId: toInt(form.referralStatusId),
        followupStatusId: toInt(form.followupStatusId),
        followupNotes: form.followupNotes || null,
        availityReferral: form.availityReferral || null,
        // Cambio 5 — Medical Director Review
        medicalDirectorStatusId: toInt(form.medicalDirectorStatusId),
        medicalDirectorFollowupId: toInt(form.medicalDirectorFollowupId),
        medicalDirectorId: toInt(form.medicalDirectorId),
        directorReviewed: form.directorReviewed || null,
        // Cambio 1 — lista de diagnósticos (el backend la orquesta en transacción)
        diagnoses: form.diagnoses.map((d) => ({
          codeDiagnosis: d.codeDiagnosis,
          isPrimary: d.isPrimary,
        })),
      };

      if (isEditing) {
        payload.specialistReferralId = Number(referralId);
        await updateReferralApi(payload);
      } else {
        await createReferralApi(payload);
      }

      navigate("/specialist-referrals", {
        state: {
          refresh: true,
          message: isEditing ? "Referral updated successfully" : "Referral created successfully",
        },
      });
    } catch (err) {
      console.error("Error saving referral:", err?.message);
      const errorMsg = err?.response?.data?.message || "Error saving referral";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/specialist-referrals");

  // Parse hedis codes string to array for Autocomplete value
  const hedisCodesArray = form.hedisCodes
    ? form.hedisCodes.split(",").map((c) => c.trim()).filter(Boolean)
    : [];

  // Cambio 1 — estado derivado de los diagnósticos
  const hasPrimaryDiagnosis = form.diagnoses.some((d) => d.isPrimary);
  const diagnosesHelperError =
    !!errors.diagnoses || (form.diagnoses.length > 0 && !hasPrimaryDiagnosis);

  // Cambio 5 — conteo de campos de Medical Director llenos
  const mdFieldsFilled = [
    form.medicalDirectorStatusId,
    form.medicalDirectorFollowupId,
    form.medicalDirectorId,
    form.directorReviewed,
  ].filter(Boolean).length;
  const mdSectionBadge = mdFieldsFilled === 4 ? "(✓)" : `(${mdFieldsFilled})`;
  const selectedDirector =
    medicalDirectorOptions.find((d) => d.medicalDirectorId === form.medicalDirectorId) || null;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handleCancel} size="large">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEditing ? "Edit Referral" : "New Specialist Referral"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? "Update referral information" : "Create a new referral to a specialist"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            size="large"
          >
            {isEditing ? "Update" : "Save"} Referral
          </Button>
        </Stack>
      </Box>

      {/* Two column layout */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 6 }}>
          {/* SECTION 1: Patient Information */}
          <Accordion expanded={expanded.patient} onChange={handleAccordionChange("patient")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Patient Information
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <PatientSearch
                    title=""
                    value={selectedPatient}
                    onSelect={handlePatientSelect}
                    helperText="Search by name and/or date of birth, then pick the patient. Only the CIP ID is used when saving."
                  />

                  {(errors.cipId || errors.memberFullName) && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.75 }}>
                      {errors.cipId || errors.memberFullName}
                    </Typography>
                  )}

                  {loadingPatient && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Loading patient details...
                      </Typography>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 2: Clinical Information */}
          <Accordion expanded={expanded.clinical} onChange={handleAccordionChange("clinical")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MedicalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Clinical Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Urgency Level"
                    value={form.urgencyLevelId}
                    onChange={(e) => handleChange("urgencyLevelId", e.target.value)}
                    error={!!errors.urgencyLevelId}
                    helperText={errors.urgencyLevelId}
                  >
                    <MenuItem value="">Select Urgency...</MenuItem>
                    {urgencyLevels.map((level) => (
                      <MenuItem key={level.statusId} value={level.statusId}>
                        {level.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Cambio 7 — Patient Code Status (CPT) */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Patient Code Status"
                    value={form.patientCodeStatusId}
                    onChange={(e) => handleChange("patientCodeStatusId", e.target.value)}
                    helperText="CPT code — new vs. existing patient (optional)"
                  >
                    <MenuItem value="">Select an option...</MenuItem>
                    {patientCodeStatusOptions.map((opt) => (
                      <MenuItem key={opt.statusId} value={opt.statusId}>
                        {opt.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Cambio 1 — Bloque de diagnósticos múltiples */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Diagnoses *
                  </Typography>

                  <Autocomplete
                    options={diagnosisOptions}
                    value={null}
                    inputValue={diagnosisInput}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${(option.code || "").trim()} — ${(option.description || "").trim()}`
                    }
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    onInputChange={(_e, value, reason) => {
                      if (reason === "reset") {
                        setDiagnosisInput("");
                        return;
                      }
                      // Los códigos ICD-10 del catálogo del backend están sin
                      // punto (p.ej. E118, no E11.8). Si el médico escribe o
                      // pega el código con punto, lo eliminamos para evitar
                      // que la búsqueda no devuelva resultados.
                      const sanitized = (value || "").replace(/\./g, "");
                      setDiagnosisInput(sanitized);
                      diagnosisSearchRef.current?.(sanitized);
                    }}
                    onChange={(_e, option) => {
                      handleAddDiagnosis(option);
                      setDiagnosisInput("");
                      setDiagnosisOptions([]);
                    }}
                    loading={diagnosisLoading}
                    noOptionsText="Type to search ICD-10 codes..."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Search ICD-10 code or description"
                        placeholder="e.g., E119 or diabetes"
                        helperText="Type at least 3 characters. ICD-10 codes must be written without dots (e.g. E119, not E11.9)."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.code}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {(option.code || "").trim()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(option.description || "").trim()}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />

                  <Box sx={{ mt: 1.5 }}>
                    {form.diagnoses.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No diagnoses added yet.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Primary</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {form.diagnoses.map((d) => (
                            <TableRow key={d.codeDiagnosis}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {d.codeDiagnosis}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {d.description || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  size="small"
                                  checked={d.isPrimary}
                                  onChange={() => handleToggleDiagnosisPrimary(d.codeDiagnosis)}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveDiagnosis(d.codeDiagnosis)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>

                  <FormHelperText error={diagnosesHelperError}>
                    {errors.diagnoses || "At least one primary diagnosis is required"}
                  </FormHelperText>
                  <Divider sx={{ mt: 1.5 }} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    freeSolo
                    options={reasonOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.referralReason
                    }
                    inputValue={form.reasonForReferral}
                    onInputChange={(_e, value) => {
                      handleChange("reasonForReferral", value || "");
                      reasonSearchRef.current?.(value);
                    }}
                    onChange={(_e, value) => {
                      if (typeof value === "string") {
                        handleChange("reasonForReferral", value);
                      } else if (value) {
                        handleChange("reasonForReferral", value.referralReason);
                      }
                    }}
                    loading={reasonLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        label="Reason for Referral"
                        error={!!errors.reasonForReferral}
                        helperText={errors.reasonForReferral}
                        multiline
                        rows={2}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.referralReasonId}>
                        <Box>
                          <Typography variant="body2">{option.referralReason}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Used {option.timesUsed} times
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={hedisOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : `${option.hedisCode} - ${option.hedisDescription}`
                    }
                    value={hedisCodesArray}
                    onInputChange={(_e, value) => {
                      hedisSearchRef.current?.(value);
                    }}
                    onChange={handleHedisChange}
                    loading={hedisLoading}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="HEDIS" placeholder="Search HEDIS codes..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={typeof option === "string" ? option : option.hedisCode}
                          size="small"
                          color="secondary"
                          {...getTagProps({ index })}
                          key={`hedis-${index}`}
                        />
                      ))
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Virtual Consult
                  </Typography>
                  <ToggleButtonGroup
                    value={form.hasRubiconBeenUsed}
                    exclusive
                    onChange={(_e, v) => handleToggle("hasRubiconBeenUsed", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Lab Required
                  </Typography>
                  <ToggleButtonGroup
                    value={form.lab}
                    exclusive
                    onChange={(_e, v) => handleToggle("lab", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Imaging Required
                  </Typography>
                  <ToggleButtonGroup
                    value={form.imaging}
                    exclusive
                    onChange={(_e, v) => handleToggle("imaging", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 4: Administrative Information */}
          <Accordion expanded={expanded.admin} onChange={handleAccordionChange("admin")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AssignmentIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Administrative Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Cambio 3 — Toggle Needs Care Manager */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Needs Care Manager?
                  </Typography>
                  <ToggleButtonGroup
                    value={form.needsCareManager}
                    exclusive
                    onChange={(_e, v) => handleToggle("needsCareManager", v)}
                    fullWidth
                  >
                    <ToggleButton value={true} color="success">
                      Yes
                    </ToggleButton>
                    <ToggleButton value={false} color="error">
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                {/* Cambio 3 — Care Manager solo visible si el toggle está activo */}
                {form.needsCareManager && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      options={careManagerOptions}
                      getOptionLabel={(option) => option?.fullName || ""}
                      value={careManagerOptions.find((cm) => cm.employeeId === form.careManagerId) || null}
                      onChange={(_e, cm) => {
                        handleChange("careManagerId", cm ? cm.employeeId : "");
                      }}
                      isOptionEqualToValue={(option, value) => option.employeeId === value?.employeeId}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Care Manager"
                          error={!!errors.careManagerId}
                          helperText={errors.careManagerId}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.employeeId}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {option.fullName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.medicalCenter || option.title || ""}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                )}

                {/* Cambio 4 — Referral Request */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Referral Request"
                    value={form.referralRequestId}
                    onChange={(e) => handleChange("referralRequestId", e.target.value)}
                    error={!!errors.referralRequestId}
                    helperText={errors.referralRequestId || "Who initiated this referral request?"}
                  >
                    <MenuItem value="">Select an option...</MenuItem>
                    {referralRequestOptions.map((opt) => (
                      <MenuItem key={opt.statusId} value={opt.statusId}>
                        {opt.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={pcpOptions}
                    getOptionLabel={(option) =>
                      option?.pcpFullName
                        ? `${option.pcpFullName}${option.title ? ", " + option.title : ""}`
                        : ""
                    }
                    value={
                      pcpOptions.find((p) => p.employeePcpId === form.pcpId) ||
                      (form.pcpId
                        ? { employeePcpId: form.pcpId, pcpFullName: form.pcpName || "" }
                        : null)
                    }
                    onChange={(_e, pcp) => {
                      setForm((prev) => ({
                        ...prev,
                        pcpId: pcp ? pcp.employeePcpId : "",
                        pcpName: pcp ? pcp.pcpFullName : "",
                      }));
                      if (errors.pcpId) setErrors((prev) => ({ ...prev, pcpId: null }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.employeePcpId === value?.employeePcpId
                    }
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Provider Name (PCP)"
                        error={!!errors.pcpId}
                        helperText={errors.pcpId || "Pre-filled from patient — change if needed"}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.employeePcpId}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.pcpFullName}
                            {option.title ? `, ${option.title}` : ""}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.medicalCenter || ""}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Center"
                    value={form.locationId}
                    onChange={(e) => handleChange("locationId", e.target.value)}
                    error={!!errors.locationId}
                    helperText={errors.locationId}
                  >
                    <MenuItem value="">Select Center...</MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc.locationId} value={loc.locationId}>
                        {loc.medicalCenter}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Staff: <strong>{user?.name || "N/A"}</strong> (auto-assigned)
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 5: Status & Follow Up */}
          <Accordion expanded={expanded.status} onChange={handleAccordionChange("status")}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HospitalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Status & Follow Up
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Status"
                    value={form.referralStatusId}
                    onChange={(e) => handleChange("referralStatusId", e.target.value)}
                    error={!!errors.referralStatusId}
                    helperText={errors.referralStatusId}
                  >
                    {referralStatuses.map((s) => (
                      <MenuItem key={s.statusId} value={s.statusId}>
                        {s.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Follow Up"
                    value={form.followupStatusId}
                    onChange={(e) => handleChange("followupStatusId", e.target.value)}
                  >
                    <MenuItem value="">Select Follow Up...</MenuItem>
                    {followupStatuses.map((s) => (
                      <MenuItem key={s.statusId} value={s.statusId}>
                        {s.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Availity Referral"
                    value={form.availityReferral}
                    onChange={(e) => handleChange("availityReferral", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Follow Up Notes"
                    value={form.followupNotes}
                    onChange={(e) => handleChange("followupNotes", e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, lg: 6 }}>
          {/* Specialist Search from Directory */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, bgcolor: "primary.50" }}>
            <Typography variant="h6" fontWeight={700} color="primary" sx={{ mb: 2 }}>
              <SearchIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Search Specialist from Directory
            </Typography>

            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Filter by Specialty"
                  value={form.specialistTypeId}
                  onChange={(e) => handleChange("specialistTypeId", e.target.value)}
                  error={!!errors.specialistTypeId}
                  helperText={errors.specialistTypeId}
                >
                  <MenuItem value="">All Specialties</MenuItem>
                  {specialistTypeOptions.map((spec) => (
                    <MenuItem key={spec.specialistTypeId} value={spec.specialistTypeId}>
                      {spec.specialistTypeName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Autocomplete
                  fullWidth
                  options={specialistDirOptions}
                  value={selectedSpecialist}
                  onChange={handleSpecialistSelect}
                  onInputChange={(_e, value) => {
                    specialistDirSearchRef.current?.(value, form.specialistTypeId);
                  }}
                  getOptionLabel={(option) => option?.specialistName || ""}
                  isOptionEqualToValue={(option, value) => option.specialistId === value?.specialistId}
                  noOptionsText="Type to search specialists..."
                  loading={specialistDirLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Search by name, NPI, or location"
                      placeholder="Type to search specialists..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.specialistId}>
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {option.specialistName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.specialistTypeName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {[option.city, option.state].filter(Boolean).join(", ")}
                          {option.npi ? ` | NPI: ${option.npi}` : ""}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              {selectedSpecialist && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" sx={{ py: 0.75 }}>
                    <Typography variant="body2" fontWeight={700}>
                      Selected: {selectedSpecialist.specialistName}
                    </Typography>
                    <Typography variant="caption">
                      {selectedSpecialist.specialistTypeName} | {selectedSpecialist.city}, {selectedSpecialist.state}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* SECTION 3: Specialist Information */}
          <Accordion expanded={expanded.specialist} onChange={handleAccordionChange("specialist")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HospitalIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Specialist Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    required
                    label="Specialist Type"
                    value={form.specialistTypeId}
                    onChange={(e) => handleChange("specialistTypeId", e.target.value)}
                    error={!!errors.specialistTypeId}
                    helperText={errors.specialistTypeId}
                  >
                    <MenuItem value="">Select Type...</MenuItem>
                    {specialistTypeOptions.map((spec) => (
                      <MenuItem key={spec.specialistTypeId} value={spec.specialistTypeId}>
                        {spec.specialistTypeName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Specialist's Name"
                    value={form.specialistName}
                    onChange={(e) => handleChange("specialistName", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="NPI"
                    value={form.specialistNpi}
                    onChange={(e) => handleChange("specialistNpi", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Specialist's Address"
                    value={form.specialistAddress}
                    onChange={(e) => handleChange("specialistAddress", e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone #"
                    value={form.specialistPhone}
                    onChange={(e) => handleChange("specialistPhone", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fax #"
                    value={form.specialistFax}
                    onChange={(e) => handleChange("specialistFax", formatPhoneNumber(e.target.value))}
                    placeholder="(000) 000-0000"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 6: Medical Director Review (Cambio 5) */}
          <Accordion
            expanded={expanded.medicalDirector}
            onChange={handleAccordionChange("medicalDirector")}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReviewIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Medical Director Review {mdSectionBadge}
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Director Status"
                    value={form.medicalDirectorStatusId}
                    onChange={(e) => handleChange("medicalDirectorStatusId", e.target.value)}
                  >
                    <MenuItem value="">Select an option...</MenuItem>
                    {mdStatusOptions.map((opt) => (
                      <MenuItem key={opt.statusId} value={opt.statusId}>
                        {opt.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Follow Up"
                    value={form.medicalDirectorFollowupId}
                    onChange={(e) => handleChange("medicalDirectorFollowupId", e.target.value)}
                  >
                    <MenuItem value="">Select an option...</MenuItem>
                    {mdFollowupOptions.map((opt) => (
                      <MenuItem key={opt.statusId} value={opt.statusId}>
                        {opt.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={medicalDirectorOptions}
                    getOptionLabel={(option) => option?.fullName || ""}
                    value={selectedDirector}
                    onChange={(_e, director) => {
                      handleChange("medicalDirectorId", director ? director.medicalDirectorId : "");
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.medicalDirectorId === value?.medicalDirectorId
                    }
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Reviewed by"
                        helperText={selectedDirector?.email ? `📧 ${selectedDirector.email}` : ""}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.medicalDirectorId}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.fullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email || ""}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date Reviewed"
                    value={form.directorReviewed}
                    onChange={(e) => handleChange("directorReviewed", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Comments Section */}
          <Accordion expanded={expanded.comments} onChange={handleAccordionChange("comments")}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CommentIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Comments ({comments.length})
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setCommentDialogOpen(true)}
                  size="small"
                >
                  Add Comment
                </Button>
              </Box>

              {comments.length === 0 ? (
                <Alert severity="info">No comments yet. Click "Add Comment" to add one.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Comment</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Staff</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comments.map((c) => (
                      <TableRow key={c.commentId}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(c.createDate)}
                        </TableCell>
                        <TableCell>{c.comment}</TableCell>
                        <TableCell>{c.createdBy}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteComment(c.commentId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Add Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Enter your comment..."
            inputProps={{ maxLength: 2000 }}
            helperText={`${newComment.length}/2000 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
